import React, { useEffect, useState } from 'react';
import { Image } from 'antd';
import { useTranslation } from '../../i18n';
import {
  CheckOutlined,
  ExclamationCircleFilled,
  LoadingOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import type { ChatMessage, ChatMessageStatus, ChatParticipant, ChatStatusLabels } from './chatTypes';
import { formatMessageTime } from './chatUtils';

type ChatBubbleProps = {
  message: ChatMessage;
  participant: ChatParticipant;
  isRunEnd: boolean;
  onResend?: (message: ChatMessage) => void;
  statusLabels?: ChatStatusLabels;
};

export function ChatAvatar({
  participant,
  size = 28,
  rounded = 'full',
}: {
  participant: ChatParticipant;
  size?: number;
  rounded?: 'full' | 'lg';
}) {
  const avatarUrl = participant.avatarUrl?.trim() || '';
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
  }, [avatarUrl]);
  const shape = rounded === 'full' ? 'rounded-full' : 'rounded-lg';
  if (avatarUrl && !failed) {
    return (
      <img
        src={avatarUrl}
        alt=""
        className={`inline-block shrink-0 object-cover ${shape}`}
        style={{ width: size, height: size }}
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <span
      className={`inline-flex shrink-0 select-none items-center justify-center font-semibold text-white ${shape}`}
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.42),
        backgroundColor: participant.color,
      }}
      aria-hidden
    >
      {participant.initial}
    </span>
  );
}

function MessageStatus({
  message,
  onResend,
  labels,
}: {
  message: ChatMessage;
  onResend?: (message: ChatMessage) => void;
  labels?: ChatStatusLabels;
}) {
  const { t } = useTranslation();
  const { status } = message;
  if (!status) {
    return null;
  }
  if (status === 'sending') {
    return <LoadingOutlined className="text-[11px] text-slate-400" aria-label={t('chat.sending')} />;
  }
  if (status === 'failed') {
    return (
      <button
        type="button"
        className="inline-flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 text-[11px] text-red-500 hover:text-red-600"
        onClick={() => onResend?.(message)}
        disabled={!onResend}
      >
        <ExclamationCircleFilled />
        {onResend ? t('chat.send_failed_retry') : t('chat.send_failed')}
      </button>
    );
  }
  const statusText: Record<Exclude<ChatMessageStatus, 'sending' | 'failed'>, string> = {
    sent: t('chat.sent'),
    delivered: t('chat.delivered'),
    read: t('chat.read'),
  };
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[11px] ${status === 'read' ? 'text-[#596ae8]' : 'text-slate-400'
        }`}
    >
      <CheckOutlined className="text-[10px]" />
      {labels?.[status] ?? statusText[status]}
    </span>
  );
}

function BubbleContent({ message }: { message: ChatMessage }) {
  const { t } = useTranslation();
  if (message.contentType === 'image') {
    return (
      <Image
        src={message.content}
        alt=""
        width={200}
        className="block rounded-lg"
        preview={{ mask: t('chat.view') }}
      />
    );
  }
  if (message.contentType === 'file') {
    return (
      <a
        href={message.content}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1.5 underline-offset-2 hover:underline"
      >
        <PaperClipOutlined />
        {message.fileName ?? t('chat.file')}
      </a>
    );
  }
  return <span className="whitespace-pre-wrap break-words">{message.content}</span>;
}

export function ChatBubble({ message, participant, isRunEnd, onResend, statusLabels }: ChatBubbleProps) {
  const isOut = message.direction === 'out';
  const isImage = message.contentType === 'image';

  const bubbleClass = isImage
    ? 'overflow-hidden rounded-xl'
    : isOut
      ? 'chat-bubble--out rounded-2xl rounded-tr-md px-3.5 py-2.5 text-white'
      : 'rounded-2xl rounded-bl-md border border-[#e5e7eb] bg-white px-3.5 py-2.5 text-slate-800';

  return (
    <div
      className={`flex items-start gap-2 ${isOut ? 'flex-row-reverse' : 'flex-row'} ${isRunEnd ? 'mb-3' : 'mb-1'
        }`}
    >
      {isRunEnd ? <ChatAvatar participant={participant} /> : <span className="w-7 shrink-0" />}
      <div className={`flex max-w-[70%] flex-col ${isOut ? 'items-end' : 'items-start'}`}>
        <div className={`text-sm leading-relaxed ${bubbleClass}`}>
          {message.subject ? (
            <div
              className={`mb-1 border-b pb-1 text-xs font-semibold ${isOut ? 'border-white/25 text-white/85' : 'border-[#eef0f4] text-slate-500'
                }`}
            >
              {message.subject}
            </div>
          ) : null}
          <BubbleContent message={message} />
        </div>
        {isRunEnd || (isOut && message.status === 'failed') ? (
          <div
            className={`mt-1 flex items-center gap-2 px-1 text-[11px] text-slate-400 ${isOut ? 'flex-row-reverse' : 'flex-row'
              }`}
          >
            {isRunEnd ? <span>{formatMessageTime(message.createdAt)}</span> : null}
            {isOut ? <MessageStatus message={message} onResend={onResend} labels={statusLabels} /> : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
